import React, { useEffect, useState } from 'react'
import { t } from 'ttag'
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@material-ui/core'
import YumSwitch from '../../components/YumSwitch/YumSwitch'
import YumButton from '../../components/YumButton/YumButton'
import YumInput from '../../components/YumInput/YumInput'
import YumDialog from '../../components/YumDialog/YumDialog'
import YumDialogTitle from '../../components/YumDialogTitle/YumDialogTitle'
import YumDialogContent from '../../components/YumDialogContent/YumDialogContent'
import YumDialogActions from '../../components/YumDialogActions/YumDialogActions'
import YumCancelButton from '../../components/YumCancelButton/YumCancelButton'
import YumSubmitButton from '../../components/YumSubmitButton/YumSubmitButton'
import YumSearchField from '../../components/YumSearchField/YumSearchField'
import { Card } from '../../components/Card/Card'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import YumSmallSelect from '../../components/YumSmallSelect/YumSmallSelect'
import YumHeader from '../../components/YumHeader/YumHeader'
import { UserCompanyDto } from '../../services/api.types'
import { FeatureFlag } from './store/FeaturesAdminReducer'
import { darkBlue } from '../../styles/material-ui-theme-v2'

interface FeatureMeta {
  label: string
  description?: string
  deprecated?: boolean
}

const FEATURE_META: Record<string, FeatureMeta> = {
  ALT_DRAG_AND_DROP: {
    label: 'Alt drag and drop',
    description:
      'Replace drag-and-drop reordering with accessible move up/down buttons (challenge admin leaderboards step)',
  },
  ANNOUNCEMENTS_ADMIN: { label: 'Announcements admin' },
  ASSIGN_RANDOM_TEAMS: { label: 'Assign random teams' },
  BACKGROUND_EXPORTS: {
    label: 'Background exports',
    description:
      'Run long exports (chat images, challenge results) in the background and email a download link when done',
  },
  BONUS_POINTS: { label: 'Bonus points' },
  BONUS_POINTS_FEATURE: { label: 'Bonus points feature', deprecated: true },
  CHALLENGE_AUTO_JOIN_OPTION: { label: 'Challenge auto-join option' },
  CHALLENGE_SEARCH: { label: 'Challenge search' },
  CHAT_ALL_UPLOAD: { label: 'Chat all upload' },
  CHAT_IMAGES_EXPORT: { label: 'Chat images export' },
  CHAT_THREADS: { label: 'Chat threads' },
  CHAT_VIDEO_UPLOAD: { label: 'Chat video upload' },
  COUNTRIES_ADMIN: { label: 'Countries admin', description: 'Show "Countries" section in admin menu' },
  DISABLE_OFFLINE_MESSAGE: { label: 'Disable offline message' },
  DOMAINS_ADMIN: { label: 'Domains admin', description: 'Show "Domains" section in admin menu' },
  FITBIT_DEPRECATION_NOTICE: {
    label: 'Fitbit deprecation notice',
    description:
      'Warn Fitbit users that the API is deprecated and they should switch to another integration (e.g. Google Health)',
  },
  GENERAL_ADMIN: { label: 'General admin', description: 'Show "General" section in admin menu' },
  GENERATE_TEAMS: { label: 'Generate teams' },
  GOOGLE_FIT_DEPRECATION_NOTICE: {
    label: 'Google Fit deprecation notice',
    description:
      'Warn Google Fit users that the API is deprecated and they should switch to another integration (e.g. Google Health)',
  },
  GOOGLE_HEALTH_BETA: {
    label: 'Google Health (beta)',
    description: 'Show the Google Health integration option (successor of the Fitbit Web API)',
  },
  GROUP_CHAT: {
    label: 'Group chat',
    description:
      'Enable the Chat view in the main menu with group chats. Replaces the challenge chat tab with a button.',
  },
  HIDE_FITBIT: {
    label: 'Hide Fitbit',
    description:
      'Hide the Fitbit integration option (also for users currently on Fitbit) for companies migrated to Google Health',
  },
  HIDE_GOOGLE_FIT: {
    label: 'Hide Google Fit',
    description:
      'Hide the Google Fit integration option (also for users currently on Google Fit), overriding "Show old Google Fit"',
  },
  HIDE_HEALTH_CONNECT: { label: 'Hide Health Connect' },
  HIDE_HEALTH_CONNECT_SETTINGS_LINK: { label: 'Hide Health Connect settings link' },
  HIDE_PROGRESS_GOALS: {
    label: 'Hide progress goals',
    description: 'Hide daily goal indicators (goal line, met-day highlights) from the progress view',
  },
  HIDE_SETTINGS_ACTIVITY_INDEX_CTA: { label: 'Hide settings activity index CTA' },
  LEAN: { label: 'Lean', description: 'Hide "Activity Index" (gender, weight etc) step from onboarding' },
  LIMIT_MANUAL_ENTRY_MIN: { label: 'Limit manual entry (min)', deprecated: true },
  LIMIT_MANUAL_ENTRY_TIME: { label: 'Limit manual entry (time)' },
  LIMIT_MANUAL_ENTRY_TO_DAILY_MAX: { label: 'Limit manual entry to daily max' },
  NEW_USER_ADMIN: { label: 'New user admin', deprecated: true },
  ONE_ACTIVE_CHALLENGE_PER_USER: {
    label: 'One active challenge per user',
    description:
      'Restrict each user to one active challenge at a time per company. Users cannot join a challenge that overlaps in time with another challenge they have already joined.',
  },
  OPT_OUT_NEWSLETTER: { label: 'Opt out newsletter' },
  OPT_OUT_REVIEWS: { label: 'Opt out reviews' },
  PHONE_FIELD: { label: 'Phone field' },
  PHONE_REQUIRED: { label: 'Phone required' },
  POINTS_CHALLENGE: { label: 'Points challenge', deprecated: true },
  REQUIRE_ACTIVITY_INDEX_VIEW: { label: 'Require activity index view' },
  SHOW_ACTIVITIES: { label: 'Show activities', deprecated: true },
  SHOW_OLD_GOOGLE_FIT: { label: 'Show old Google Fit' },
  SLEEP_SYNC: { label: 'Sleep sync' },
  SORT_FUTURE_CHALLENGES_BY_START_DATE: { label: 'Sort future challenges by start date' },
  SSN_FIELD: { label: 'SSN field' },
  SSN_LAST_FOUR: { label: 'SSN last four' },
  SSN_REQUIRED: { label: 'SSN required' },
  SSO_ADMIN: { label: 'SSO Admin' },
  SYNC_ACTIVITIES: { label: 'Sync activities' },
  TEAM_ADMIN: { label: 'Team admin', deprecated: true },
  TOTAL_MAX: { label: 'Total max' },
  USE_AUTOCOMPLETE_SELECT: { label: 'Use autocomplete select' },
  USER_COMPANIES: { label: 'User companies', deprecated: true },
  SIGN_UP_CODE_TEAM: {
    label: 'Sign-up code team',
    description: 'Allow creating sign-up codes which register users automatically into specific team',
  },
  IMPORT_USERS_ADMIN: {
    label: 'Import users admin',
    description: 'Allow admins to import users via Excel file upload',
  },
  IMPORT_DATA_ADMIN: {
    label: 'Import data admin',
    description: 'Allow admins to import activity data via Excel file upload',
  },
  UNLIMITED_POINTS_ACTIVITIES: {
    label: 'Unlimited points activities',
    description: 'Remove the 20-activity limit per wellness points challenge',
  },
  UNLIMITED_POINTS_VALUE: {
    label: 'Unlimited points value',
    description: 'Remove the 100000 maximum on a points challenge activity value',
  },
  UNLIMITED_POINTS_PER_ACTIVITY: {
    label: 'Unlimited points per activity',
    description: 'Remove the 100 maximum points awarded per points challenge activity',
  },
  EXCLUDE_APPLE_EXERCISE_MINUTES: {
    label: 'Exclude Apple exercise minutes',
    description:
      'Do not count Apple Health exercise minutes toward the EXERCISE_MIN metric (calculate from logged activities only)',
  },
  EXCLUDE_GOOGLE_FIT_MOVE_MINUTES: {
    label: 'Exclude Google Fit move minutes',
    description:
      'Do not count Google Fit move minutes toward the EXERCISE_MIN metric (calculate from logged activities only)',
  },
  LOCATION_TRACKING: {
    label: 'Location tracking',
    description:
      'Allow GPS/location data: in-app route recording, integration route sync (e.g. Garmin), and showing routes + location names. When off, no location is tracked, synced, stored, or shown.',
  },
  /*VIEW_ACTIVITIES: {
    label: 'View activities',
    description:
      'Show the Activities view (activity list) in the app. When off, the nav item is hidden and the page is inaccessible.',
    // Will be available in future version
    deprecated: true,
  },*/
  VIEW_ACTIVITIES_BETA: {
    label: 'View activities (beta)',
    description:
      'Show the reworked Activities view (activity list) in current app versions. When off, the nav item is hidden and the page is inaccessible.',
  },
  RECORD_ACTIVITY: {
    label: 'Record activity',
    description: 'Allow in-app workout recording from the Activities view. Only has effect when View activities is on.',
  },
}

const getFeatureMeta = (key: string): FeatureMeta => {
  if (FEATURE_META[key]) return FEATURE_META[key]
  // Fallback: convert SCREAMING_SNAKE_CASE to Title Case
  return {
    label: key
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase()),
  }
}

// --- Props ---
export interface IFeaturesAdminProps {
  managedCompanies: UserCompanyDto[]
  features: FeatureFlag[]
  loading: boolean
  saving: boolean
  selectedCompanyId: string | null
  isSuperAdmin: boolean
}

export interface IFeaturesAdminDispatchProps {
  getCompanyFeatureFlags: (payload: { companyId: string }) => void
  updateCompanyFeatureFlags: (payload: { companyId: string; features: Record<string, boolean>; secret: string }) => void
  setFeaturesSelectedCompanyId: (payload: string) => void
}

type FeaturesAdminProps = IFeaturesAdminProps & IFeaturesAdminDispatchProps

export default function FeaturesAdmin(props: FeaturesAdminProps) {
  const {
    managedCompanies,
    features,
    loading,
    saving,
    selectedCompanyId,
    isSuperAdmin,
    getCompanyFeatureFlags,
    updateCompanyFeatureFlags,
    setFeaturesSelectedCompanyId,
  } = props

  // Local dirty state: tracks toggled flags
  const [localFlags, setLocalFlags] = useState<Record<string, boolean>>({})
  // Search
  const [search, setSearch] = useState('')
  // Filter: only show features that differ from global default
  const [showOnlyOverrides, setShowOnlyOverrides] = useState(false)
  // Secret modal state
  const [secretDialogOpen, setSecretDialogOpen] = useState(false)
  const [secret, setSecret] = useState('')

  const companyId = selectedCompanyId || (managedCompanies.length > 0 ? managedCompanies[0].companyId : null)

  // Set default company on first mount
  useEffect(() => {
    if (companyId && !selectedCompanyId) {
      setFeaturesSelectedCompanyId(companyId)
    }
  }, [])

  // Load features whenever selected company changes
  useEffect(() => {
    if (companyId) {
      getCompanyFeatureFlags({ companyId })
      setLocalFlags({})
    }
  }, [companyId])

  // Reset local flags when features reload from server
  useEffect(() => {
    setLocalFlags({})
  }, [features])

  const handleCompanyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newCompanyId = event.target.value as string
    setFeaturesSelectedCompanyId(newCompanyId)
    getCompanyFeatureFlags({ companyId: newCompanyId })
    setLocalFlags({})
  }

  const handleToggle = (key: string, currentEnabled: boolean) => {
    setLocalFlags((prev) => {
      const original = features.find((f) => f.key === key)
      const newValue = !currentEnabled
      // If toggling back to original value, remove from dirty state
      if (original && original.enabled === newValue) {
        const next = { ...prev }
        delete next[key]
        return next
      }
      return { ...prev, [key]: newValue }
    })
  }

  const hasChanges = Object.keys(localFlags).length > 0

  const handleSaveClick = () => {
    if (isSuperAdmin) {
      if (!companyId) return
      updateCompanyFeatureFlags({ companyId, features: localFlags, secret: '' })
      return
    }
    setSecret('')
    setSecretDialogOpen(true)
  }

  const handleSecretDialogClose = () => {
    if (!saving) {
      setSecretDialogOpen(false)
      setSecret('')
    }
  }

  const handleConfirmSave = () => {
    if (!companyId || !secret.trim()) return

    // Only send changed flags
    updateCompanyFeatureFlags({ companyId, features: localFlags, secret: secret.trim() })
    setSecretDialogOpen(false)
    setSecret('')
  }

  const handleSecretKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && secret.trim()) {
      handleConfirmSave()
    }
  }

  // Resolve the effective enabled state for a feature
  const getEffectiveEnabled = (f: FeatureFlag) => (f.key in localFlags ? localFlags[f.key] : f.enabled)

  const filteredFeatures = features.filter((f) => {
    const meta = getFeatureMeta(f.key)
    // Hide deprecated flags unless they have a non-default override
    if (meta.deprecated && f.enabled === f.defaultEnabled && !(f.key in localFlags)) return false
    if (showOnlyOverrides) {
      const serverOverridden = f.enabled !== f.defaultEnabled
      const locallyChanged = f.key in localFlags
      if (!serverOverridden && !locallyChanged) return false
    }
    if (search) {
      const q = search.toLowerCase()
      return f.key.toLowerCase().includes(q) || meta.label.toLowerCase().includes(q)
    }
    return true
  })

  return (
    <Card>
      <NotificationsWrapper />

      <YumHeader>
        <h1>{t`Features`}</h1>
        {managedCompanies.length > 1 && (
          <YumSmallSelect
            variant="outlined"
            value={companyId}
            onChange={handleCompanyChange}
            inputProps={{ 'aria-label': t`Company`, 'data-a11y-label': t`Company` }}
            className="show-on-desktop"
          >
            {managedCompanies.map((userCompany: UserCompanyDto, keyIndex: number) => (
              <option key={keyIndex} value={userCompany.companyId}>
                {userCompany.companyName}
              </option>
            ))}
          </YumSmallSelect>
        )}
      </YumHeader>
      {managedCompanies.length > 1 && (
        <YumSmallSelect
          variant="outlined"
          value={companyId}
          onChange={handleCompanyChange}
          inputProps={{ 'aria-label': t`Company`, 'data-a11y-label': t`Company` }}
          className="hide-on-desktop"
          fullWidth
          style={{ marginBottom: 16 }}
        >
          {managedCompanies.map((userCompany: UserCompanyDto, keyIndex: number) => (
            <option key={keyIndex} value={userCompany.companyId}>
              {userCompany.companyName}
            </option>
          ))}
        </YumSmallSelect>
      )}

      {!loading && features.length > 0 && (
        <>
          <YumSearchField
            value={search}
            onChange={setSearch}
            autoFocus={false}
            debounceTime={200}
            style={{ marginBottom: 4 }}
          />
          <Box style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyOverrides}
                  onChange={() => setShowOnlyOverrides((v) => !v)}
                  color="primary"
                  size="small"
                />
              }
              label={t`Only show modified`}
              style={{ marginRight: 0 }}
            />
          </Box>

          <FormGroup>
            {filteredFeatures.map((feature) => {
              const checked = getEffectiveEnabled(feature)
              const meta = getFeatureMeta(feature.key)
              const isOverridden = checked !== feature.defaultEnabled
              return (
                <ListItem
                  key={feature.key}
                  role={undefined}
                  dense
                  button
                  style={{ paddingLeft: 0 }}
                  onClick={() => handleToggle(feature.key, checked)}
                >
                  <ListItemText
                    primary={
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{meta.label}</span>
                        {isOverridden && (
                          <Chip
                            label={feature.defaultEnabled ? t`default: ON` : t`default: OFF`}
                            size="small"
                            style={{
                              height: 20,
                              fontSize: '0.6875rem',
                              fontWeight: 600,
                              margin: 0,
                              verticalAlign: 'middle',
                              backgroundColor: feature.defaultEnabled ? '#fff3e0' : '#fce4ec',
                              color: feature.defaultEnabled ? darkBlue : '#c62828',
                            }}
                          />
                        )}
                        {meta.deprecated && (
                          <Chip
                            label={t`Deprecated`}
                            size="small"
                            style={{
                              height: 20,
                              fontSize: '0.6875rem',
                              fontWeight: 600,
                              margin: 0,
                              verticalAlign: 'middle',
                              backgroundColor: '#eeeeee',
                              color: darkBlue,
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={meta.description}
                    secondaryTypographyProps={
                      meta.description ? { style: { fontSize: '0.875rem', marginTop: '4px' } } : undefined
                    }
                  />
                  <ListItemIcon style={{ minWidth: 'auto' }}>
                    <YumSwitch checked={checked} />
                  </ListItemIcon>
                </ListItem>
              )
            })}
          </FormGroup>
        </>
      )}

      {!loading && features.length === 0 && (
        <Box style={{ padding: '24px 0', color: '#999' }}>{t`No feature flags found.`}</Box>
      )}

      {!loading && features.length > 0 && (
        <Box style={{ marginTop: 16 }}>
          <YumButton color="primary" variant="contained" disabled={!hasChanges || saving} onClick={handleSaveClick}>
            {t`Save changes`}
          </YumButton>
        </Box>
      )}

      {/* Secret key confirmation dialog */}
      <YumDialog open={secretDialogOpen}>
        <YumDialogTitle handleClose={handleSecretDialogClose}>{t`Enter secret key`}</YumDialogTitle>
        <YumDialogContent>
          <YumInput
            autoFocus
            label={t`Secret key`}
            value={secret}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSecret(e.target.value)}
            onKeyDown={handleSecretKeyDown}
            type="password"
            style={{ marginBottom: 0 }}
            disabled={saving}
          />
        </YumDialogContent>
        <YumDialogActions>
          <YumCancelButton onClick={handleSecretDialogClose} disabled={saving} />
          <YumSubmitButton
            text={t`Confirm`}
            onClick={handleConfirmSave}
            disabled={saving || !secret.trim()}
            submitInProgress={saving}
            immersiveLoader={true}
          />
        </YumDialogActions>
      </YumDialog>
    </Card>
  )
}
